import { useId, useMemo, useState, useEffect, useRef, useCallback } from "react";
import { torontoDateParts } from "@utils";
import Button from "./Button";

const MONTHS = [
  { key: "01", label: "Jan" },
  { key: "02", label: "Feb" },
  { key: "03", label: "Mar" },
  { key: "04", label: "Apr" },
  { key: "05", label: "May" },
  { key: "06", label: "Jun" },
  { key: "07", label: "Jul" },
  { key: "08", label: "Aug" },
  { key: "09", label: "Sep" },
  { key: "10", label: "Oct" },
  { key: "11", label: "Nov" },
  { key: "12", label: "Dec" },
];

function formatMonthShort(value) {
  if (!value) return "";
  const [y, m] = String(value).split("-");
  const label = MONTHS.find((x) => x.key === m)?.label || m;
  return `${label} ${y}`;
}

/**
 * Multi-month picker (YYYY-MM[]) with year navigation and "disable current & future".
 * Renders a Year navigator with a 3x4 grid of month checkboxes.
 *
 * value: string[] of "YYYY-MM"
 * onChange: (next: string[]) => void
 */
export default function MultiMonthPicker({
  value = [],
  onChange,
  disableCurrentAndFuture = true,
  className = "",
  id: idProp,
  placeholder = "Select months",
  maxLabelCount = 3,
}) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const now = useMemo(() => torontoDateParts(), []);
  const currentYear = now.year;
  const currentMonthIndex = now.month; // 1..12
  const allowCurrentMonth = now.day >= 25;

  const selectedSet = useMemo(() => new Set(value || []), [value]);
  // Default panel year = year of the most recent selection, else current.
  const defaultYear = useMemo(() => {
    if (!value || value.length === 0) return currentYear;
    const years = value.map((v) => Number(String(v).slice(0, 4))).filter((n) => Number.isFinite(n));
    return years.length ? Math.max(...years) : currentYear;
  }, [value, currentYear]);

  const [panelYear, setPanelYear] = useState(defaultYear);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (open) setPanelYear(defaultYear);
  }, [open, defaultYear]);

  useEffect(() => {
    function onDocClick(e) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const isDisabledMonth = useCallback(
    (year, monthKey) => {
      if (!disableCurrentAndFuture) return false;
      const m = Number(monthKey);
      if (year > currentYear) return true;
      if (year === currentYear && m > currentMonthIndex) return true;
      if (year === currentYear && m === currentMonthIndex) return !allowCurrentMonth;
      return false;
    },
    [allowCurrentMonth, currentMonthIndex, currentYear, disableCurrentAndFuture],
  );

  const toggle = useCallback(
    (year, monthKey) => {
      if (isDisabledMonth(year, monthKey)) return;
      const v = `${year}-${monthKey}`;
      const next = new Set(selectedSet);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      const sorted = Array.from(next).sort();
      onChange?.(sorted);
    },
    [isDisabledMonth, onChange, selectedSet],
  );

  const selectAllYear = useCallback(() => {
    const next = new Set(selectedSet);
    MONTHS.forEach((m) => {
      if (!isDisabledMonth(panelYear, m.key)) next.add(`${panelYear}-${m.key}`);
    });
    onChange?.(Array.from(next).sort());
  }, [isDisabledMonth, onChange, panelYear, selectedSet]);

  const clearAll = useCallback(() => onChange?.([]), [onChange]);

  const sortedSelected = useMemo(() => Array.from(selectedSet).sort(), [selectedSet]);
  const prettyValue = useMemo(() => {
    if (sortedSelected.length === 0) return placeholder;
    if (sortedSelected.length <= maxLabelCount) {
      return sortedSelected.map(formatMonthShort).join(", ");
    }
    return `${sortedSelected.length} months selected`;
  }, [maxLabelCount, placeholder, sortedSelected]);

  const canGoNextYear = !disableCurrentAndFuture || panelYear < currentYear;

  return (
    <div className={`relative w-full ${className}`} ref={wrapRef}>
      <Button
        id={id}
        type="button"
        variant="outline"
        size="md"
        onClick={() => setOpen((v) => !v)}
        className="mt-0 w-full max-w-[240px] justify-between"
      >
        <span className="font-semibold truncate">{prettyValue}</span>
        <span className="text-palette-gray-secondary ml-2">▾</span>
      </Button>

      {open && (
        <div className="absolute z-50 mt-2 w-[300px] rounded-2xl border border-palette-gray-divider bg-white p-4 shadow-xl">
          <div className="flex items-center justify-between mb-3">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setPanelYear((y) => y - 1)}
              className="h-9 w-9 !px-0 !py-0 border border-palette-gray-divider rounded-lg hover:bg-palette-gray-soft"
              aria-label="Previous year"
            >
              ◀
            </Button>
            <div className="text-sm font-bold">{panelYear}</div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={!canGoNextYear}
              onClick={() => setPanelYear((y) => y + 1)}
              className="h-9 w-9 !px-0 !py-0 border border-palette-gray-divider rounded-lg hover:bg-palette-gray-soft"
              aria-label="Next year"
            >
              ▶
            </Button>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {MONTHS.map((m) => {
              const disabled = isDisabledMonth(panelYear, m.key);
              const v = `${panelYear}-${m.key}`;
              const checked = selectedSet.has(v);
              return (
                <label
                  key={m.key}
                  className={[
                    "flex items-center gap-2 rounded-lg border px-2 py-1.5 text-sm",
                    disabled
                      ? "opacity-40 cursor-not-allowed border-palette-gray-divider"
                      : "cursor-pointer border-palette-gray-divider hover:bg-palette-gray-soft",
                    checked && !disabled
                      ? "bg-palette-indigo-soft border-palette-indigo-hover"
                      : "",
                  ].join(" ")}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => toggle(panelYear, m.key)}
                  />
                  <span>{m.label}</span>
                </label>
              );
            })}
          </div>

          <div className="mt-3 flex items-center justify-between text-xs">
            <button
              type="button"
              onClick={selectAllYear}
              className="text-palette-indigo-badge hover:text-palette-indigo-text"
            >
              Select all {panelYear}
            </button>
            <button
              type="button"
              onClick={clearAll}
              className="text-palette-gray-secondary hover:text-palette-gray-heading"
              disabled={sortedSelected.length === 0}
            >
              Clear all
            </button>
          </div>

          {disableCurrentAndFuture && (
            <div className="mt-2 text-[11px] text-palette-gray-secondary">
              Current &amp; future months are disabled.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
